/**
 * ML Studio Page - The Robot Gym
 * ===============================
 * Train machine learning models with PostgresML!
 * Like teaching robots to learn patterns.
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Container, 
  Plus, 
  Brain,
  Cpu,
  Activity,
  Terminal,
  LogOut,
  Flame,
  Zap,
  Database,
  Play,
  BarChart3,
  Loader2,
  TrendingUp,
  Target,
  Check,
  AlertCircle,
  Dumbbell,
  Trophy
} from "lucide-react";
import { toast } from "sonner";

// Sidebar Component
const Sidebar = ({ activePage }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menuItems = [
    { id: "dashboard", label: "MISSION CONTROL", icon: <Activity className="w-5 h-5" />, path: "/dashboard" },
    { id: "sites", label: "DEPLOYMENTS", icon: <Container className="w-5 h-5" />, path: "/sites" },
    { id: "ml-studio", label: "ROBOT GYM", icon: <Brain className="w-5 h-5" />, path: "/ml-studio" },
    { id: "tutorials", label: "GUIDES", icon: <Trophy className="w-5 h-5" />, path: "/tutorials" },
    { id: "family", label: "FAMILY", icon: <Flame className="w-5 h-5" />, path: "/family" },
  ];

  return (
    <div className="w-64 bg-[#16191F] border-r border-white/5 flex flex-col min-h-screen">
      <div className="p-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
            <Terminal className="w-6 h-6 text-black" />
          </div>
          <span className="text-xl font-black text-white tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            CODEFORGE
          </span>
        </div>
      </div>

      <div className="p-4 m-4 bg-black/30 border border-primary/20">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono text-muted-foreground uppercase">LEVEL {user?.level || 1}</span>
          <span className="text-xs font-mono text-primary">{user?.xp || 0} XP</span>
        </div>
        <Progress value={(user?.xp || 0) % 100} className="h-2 bg-muted" />
        <div className="flex items-center gap-2 mt-3">
          <Flame className="w-4 h-4 text-orange-400" />
          <span className="text-xs font-mono text-white">{user?.streak || 0} day streak</span>
        </div>
      </div>

      <nav className="flex-1 p-4">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-mono transition-colors ${
                activePage === item.id
                  ? "bg-primary/10 text-primary border-l-2 border-primary"
                  : "text-muted-foreground hover:text-white hover:bg-white/5"
              }`}
              data-testid={`nav-${item.id}`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
              <span className="text-primary font-bold text-sm">{user?.username?.[0]?.toUpperCase()}</span>
            </div>
            <span className="text-sm font-mono text-white">{user?.username}</span>
          </div>
          <button onClick={logout} className="text-muted-foreground hover:text-red-400 transition-colors" data-testid="logout-btn">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Model Card Component
const ModelCard = ({ model }) => {
  const statusColors = {
    ready: "bg-green-500",
    training: "bg-yellow-500 animate-pulse",
    error: "bg-red-500"
  };

  const statusLabels = {
    ready: "READY",
    training: "TRAINING...",
    error: "ERROR"
  };

  return (
    <div className="bg-[#16191F] border border-white/5 p-6 hover:border-purple-400/50 transition-all">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Brain className="w-6 h-6 text-purple-400" />
          <h3 className="font-bold text-white">{model.name}</h3>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${statusColors[model.status]}`} />
          <span className="text-xs font-mono text-muted-foreground">{statusLabels[model.status]}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <span className="text-xs font-mono text-muted-foreground">TYPE</span>
          <p className="text-white font-mono text-sm capitalize">{model.model_type}</p>
        </div>
        <div>
          <span className="text-xs font-mono text-muted-foreground">ACCURACY</span>
          <p className="text-green-400 font-mono text-sm">
            {model.accuracy ? `${(model.accuracy * 100).toFixed(1)}%` : '--'}
          </p>
        </div>
      </div>

      {model.accuracy && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs font-mono mb-1">
            <span className="text-muted-foreground">Model Accuracy</span>
            <span className="text-green-400">{(model.accuracy * 100).toFixed(1)}%</span>
          </div>
          <Progress value={model.accuracy * 100} className="h-2 bg-muted" />
        </div>
      )}
    </div>
  );
};

const MLStudioPage = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTrainModal, setShowTrainModal] = useState(false);
  const [training, setTraining] = useState(false);
  const [newModel, setNewModel] = useState({
    name: "",
    model_type: "regression",
    training_data: "training_data",
  });

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      const res = await api.get("/ml/models");
      setModels(res.data);
    } catch (error) {
      toast.error("Failed to load models");
    } finally {
      setLoading(false);
    }
  };

  const handleTrainModel = async (e) => {
    e.preventDefault();
    setTraining(true);
    try {
      const res = await api.post("/ml/models", newModel);
      setModels([res.data, ...models]);
      setShowTrainModal(false);
      setNewModel({ name: "", model_type: "regression", training_data: "training_data" });
      toast.success("Model trained successfully!", { description: "+50 XP earned!" });
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to train model");
    } finally {
      setTraining(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#0B0D10]">
      <Sidebar activePage="ml-studio" />
      
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <header className="border-b border-white/5 bg-[#16191F]/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="px-8 py-4 flex items-center justify-between">
            <div>
              <h1 
                className="text-2xl font-bold text-white uppercase tracking-tight flex items-center gap-3"
                style={{ fontFamily: 'Chivo, sans-serif' }}
                data-testid="ml-studio-title"
              >
                <Dumbbell className="w-7 h-7 text-purple-400" />
                ROBOT GYM
              </h1>
              <p className="text-xs font-mono text-muted-foreground mt-1">
                PostgresML Training Center - {models.length} model{models.length !== 1 ? 's' : ''} trained
              </p>
            </div>
            <Button
              onClick={() => setShowTrainModal(true)}
              className="bg-green-500 text-black font-bold hover:bg-green-400"
              data-testid="train-model-btn"
            >
              <Brain className="w-4 h-4 mr-2" />
              TRAIN NEW MODEL
            </Button>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-8">
          {/* Info Banner */}
          <div className="bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-green-500/10 border border-purple-400/20 p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-500/20">
                <Brain className="w-8 h-8 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  WELCOME TO THE ROBOT GYM!
                </h3>
                <p className="text-muted-foreground font-mono text-sm mb-4">
                  This is where you train machine learning models using PostgresML. 
                  Think of it like teaching robots to recognize patterns in data!
                </p>
                <div className="grid grid-cols-3 gap-4 text-sm font-mono">
                  <div className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-blue-400" />
                    <span className="text-white">In-Database ML</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    <span className="text-white">XGBoost & More</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-primary" />
                    <span className="text-white">+50 XP per model</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-[#16191F] border border-white/5 p-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Brain className="w-4 h-4" />
                <span className="text-xs font-mono uppercase">TOTAL MODELS</span>
              </div>
              <div className="text-4xl font-black text-purple-400" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {models.length}
              </div>
            </div>
            
            <div className="bg-[#16191F] border border-white/5 p-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Check className="w-4 h-4" />
                <span className="text-xs font-mono uppercase">READY MODELS</span>
              </div>
              <div className="text-4xl font-black text-green-400" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {models.filter(m => m.status === 'ready').length}
              </div>
            </div>
            
            <div className="bg-[#16191F] border border-white/5 p-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs font-mono uppercase">AVG ACCURACY</span>
              </div>
              <div className="text-4xl font-black text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {models.length > 0 && models.some(m => m.accuracy)
                  ? `${(models.filter(m => m.accuracy).reduce((acc, m) => acc + m.accuracy, 0) / models.filter(m => m.accuracy).length * 100).toFixed(0)}%`
                  : '--'}
              </div>
            </div>
          </div>

          {/* Models Grid */}
          <div className="mb-8">
            <h2 
              className="text-xl font-bold text-white uppercase mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              YOUR TRAINED MODELS
            </h2>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400 mx-auto mb-2" />
                  <p className="text-muted-foreground font-mono">Loading models...</p>
                </div>
              </div>
            ) : models.length === 0 ? (
              <div className="bg-[#16191F] border border-white/5 p-12 text-center">
                <Brain className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">No Models Yet</h3>
                <p className="text-muted-foreground font-mono text-sm mb-6">
                  Train your first model to start making predictions!
                </p>
                <Button
                  onClick={() => setShowTrainModal(true)}
                  className="bg-green-500 text-black font-bold"
                  data-testid="create-first-model-btn"
                >
                  <Brain className="w-4 h-4 mr-2" />
                  Train Your First Model
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {models.map((model) => (
                  <ModelCard key={model.id} model={model} />
                ))}
              </div>
            )}
          </div>

          {/* Frick & Frack Guide */}
          <div className="bg-[#16191F] border border-white/5 p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <AlertCircle className="w-5 h-5 text-primary" />
              FRICK & FRACK'S ML GUIDE
            </h3>
            <div className="grid md:grid-cols-2 gap-6 text-sm font-mono">
              <div>
                <h4 className="text-primary mb-2">What is Machine Learning?</h4>
                <p className="text-muted-foreground">
                  Machine Learning is like teaching a robot to recognize patterns. 
                  You show it lots of examples, and it learns to make predictions!
                </p>
              </div>
              <div>
                <h4 className="text-green-400 mb-2">What is PostgresML?</h4>
                <p className="text-muted-foreground">
                  PostgresML lets you train ML models right inside your database. 
                  No need for separate ML servers - it's all in one place!
                </p>
              </div>
              <div>
                <h4 className="text-blue-400 mb-2">Regression vs Classification</h4>
                <p className="text-muted-foreground">
                  <strong>Regression</strong> predicts numbers (like prices).<br />
                  <strong>Classification</strong> predicts categories (like "spam" or "not spam").
                </p>
              </div>
              <div>
                <h4 className="text-purple-400 mb-2">What is Accuracy?</h4>
                <p className="text-muted-foreground">
                  Accuracy tells you how often the model gets the right answer. 
                  95% accuracy means it's right 95 times out of 100!
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Train Model Modal */}
      <Dialog open={showTrainModal} onOpenChange={setShowTrainModal}>
        <DialogContent className="bg-[#16191F] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Dumbbell className="w-6 h-6 text-green-400" />
              TRAIN NEW MODEL
            </DialogTitle>
            <DialogDescription className="text-muted-foreground font-mono text-sm">
              Configure your robot's training session
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleTrainModel} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase text-muted-foreground">
                MODEL NAME
              </Label>
              <Input
                type="text"
                value={newModel.name}
                onChange={(e) => setNewModel({ ...newModel, name: e.target.value })}
                placeholder="My Awesome Model"
                className="bg-black/50 border-white/10 font-mono"
                required
                minLength={2}
                data-testid="model-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase text-muted-foreground">
                MODEL TYPE
              </Label>
              <Select
                value={newModel.model_type}
                onValueChange={(value) => setNewModel({ ...newModel, model_type: value })}
              >
                <SelectTrigger className="bg-black/50 border-white/10 font-mono" data-testid="model-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#16191F] border-white/10">
                  <SelectItem value="regression" className="font-mono">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-blue-400" />
                      Regression (predict numbers)
                    </div>
                  </SelectItem>
                  <SelectItem value="classification" className="font-mono">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-green-400" />
                      Classification (predict categories)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase text-muted-foreground">
                TRAINING DATA TABLE
              </Label>
              <Select
                value={newModel.training_data}
                onValueChange={(value) => setNewModel({ ...newModel, training_data: value })}
              >
                <SelectTrigger className="bg-black/50 border-white/10 font-mono" data-testid="training-data-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#16191F] border-white/10">
                  <SelectItem value="training_data" className="font-mono">
                    training_data (sample)
                  </SelectItem>
                  <SelectItem value="user_data" className="font-mono">
                    user_data
                  </SelectItem>
                  <SelectItem value="sales_data" className="font-mono">
                    sales_data
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-green-500/10 border border-green-500/30 p-4">
              <div className="flex items-center gap-2 text-green-400 text-sm font-mono mb-1">
                <Trophy className="w-4 h-4" />
                +50 XP REWARD
              </div>
              <p className="text-xs text-muted-foreground font-mono">
                Training a model earns you XP points!
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowTrainModal(false)}
                className="flex-1 border-white/10 font-mono"
                data-testid="cancel-train-btn"
              >
                CANCEL
              </Button>
              <Button
                type="submit"
                disabled={training}
                className="flex-1 bg-green-500 text-black font-bold hover:bg-green-400"
                data-testid="submit-train-btn"
              >
                {training ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    TRAINING...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    START TRAINING
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MLStudioPage;
